import { Box, Button, Divider, Switch, Typography } from '@mui/material';
import { useAuthStore } from '../../../../store/storeAuth';
import KitModal from '../../../UI/KitModal';
import { CubeTransparent, Info } from '@phosphor-icons/react';
import IconButtonTooltip from '../../../UI/IconButtonTooltip/IconButtonTooltip';
import { useEffect, useState } from 'react';

type Props = {
  onHide: () => void;
  show: boolean;
  data: any;
  object: any;
  onConfirm: () => void;
  onChange: (e: any) => void;
};

const SettingsModal = ({
  onHide,
  show,
  onChange,
  data,
  object,
  onConfirm,
}: Props) => {
  const [isLocal, setIsLocal] = useState(object?.localObject);
  const [isSessionEnabled, setIsSessionEnabled] = useState(
    data?.sessionEnabled || false,
  );
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    setIsLocal(object?.localObject);
  }, [show, object?.localObject]);

  useEffect(() => {
    setIsSessionEnabled(data?.sessionEnabled);
  }, [data?.sessionEnabled]);

  const confirmText = !object?.localObject
    ? 'Switching this Object to Local means it will only be usable within this agent. To not break all references to this Object from other agents we will create a local duplicate for you to use.'
    : 'It will appear in the left panel and be accessible across all agents in this space. Are you sure you want to proceed?';

  const { user } = useAuthStore(state => state);

  const handleClose = () => {
    onHide();
  };

  return (
    <>
      <KitModal
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            maxWidth: '650px',
            borderRadius: '16px',
          },
          '& .MuiDialogContent-root': {
            padding: '16px',
          },
          '& .MuiDialogActions-root': {
            padding: '16px',
          },
        }}
        onHide={handleClose}
        show={show}
      >
        <Box>
          <Typography
            variant="h5"
            color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
          >
            Settings
          </Typography>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            mt={'16px'}
            p={'8px'}
            borderRadius={'4px'}
          >
            <Box>
              <Typography
                variant="h6"
                color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
              >
                Object Type
              </Typography>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
              >
                <IconButtonTooltip
                  title={
                    'Local setting means this object can only be seen, selected, and used within this agent. It will not be visible in any other agent or on the left-navigation space.'
                  }
                >
                  <Typography color="#98A2B3" fontSize={'14px'}>
                    Local
                  </Typography>
                  <Info
                    color="#98A2B3"
                    size={12}
                    style={{ marginLeft: '4px' }}
                  />
                </IconButtonTooltip>
                <Switch
                  name="objectType"
                  size="small"
                  onChange={(e: any) => {
                    setConfirmModalOpen(true);
                  }}
                  checked={!isLocal}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#1976d2',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#1976d2',
                    },
                    '& .MuiSwitch-thumb': {
                      backgroundColor: '#1976d2',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#1976d2',
                      opacity: 0.5,
                    },
                  }}
                />
                <IconButtonTooltip
                  title={
                    'Global setting means this object can be used or referenced by any and all agents within this space.'
                  }
                >
                  <Typography color="#98A2B3" fontSize={'14px'}>
                    Global
                  </Typography>
                  <Info
                    color="#98A2B3"
                    size={12}
                    style={{ marginLeft: '4px' }}
                  />
                </IconButtonTooltip>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography
                variant="h6"
                color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
              >
                Agent Session Mode
              </Typography>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
              >
                <Switch
                  name="sessionSwitch"
                  size="small"
                  checked={isSessionEnabled}
                  onChange={e => {
                    onChange({
                      target: {
                        name: 'sessionEnabled',
                        value: e.target.checked,
                      },
                    });
                  }}
                />
                <Typography color="#98A2B3" fontSize={'14px'}>
                  Enable Agent Session Mode <br /> (Saves multiple versions of
                  the Object for chat or API sessions)
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </KitModal>

      <KitModal
        onHide={() => {
          setConfirmModalOpen(false);
        }}
        show={confirmModalOpen}
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            maxWidth: '650px',
            borderRadius: '16px',
          },
          '& .MuiDialogContent-root': {
            padding: '16px',
          },
          '& .MuiDialogActions-root': {
            padding: '16px',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                width: '64px',
                height: '64px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CubeTransparent size={64} />
            </Box>
          </Box>
          <Typography
            variant="h6"
            color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
            align="center"
          >
            You're switching this Object from{' '}
            {data?.localObject ? 'Local' : 'Global'} to{' '}
            {data?.localObject ? 'Global' : 'Local'}
          </Typography>

          <Box>
            <Typography
              color="#98A2B3"
              fontSize={'14px'}
              align="center"
              sx={{ padding: '8px 64px', whiteSpace: 'pre-line' }}
            >
              {confirmText}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '16px' }}>
          <Button
            color="error"
            variant="outlined"
            onClick={() => setConfirmModalOpen(false)}
            style={{ marginRight: '8px' }}
          >
            No, go back
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmModalOpen(false);
              onConfirm();
            }}
          >
            Yes, make {object?.localObject ? 'Global' : 'Local'}
          </Button>
        </Box>
      </KitModal>
    </>
  );
};

export default SettingsModal;
